/**
 * Shared plumbing for the extraction steps. Every step is DB-in/DB-out: it reads what it needs from
 * the exam, does its work, writes the result back, and reports its own progress. Nothing is threaded
 * between steps in memory, so a step can run at any time, in any order the exam's state allows, and
 * a restart between two steps loses nothing.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import { addSolutionChangedTasks } from '../../extraction-progress.js';
import { applyCostDelta } from '../add-cost.js';
import { examTaskTreeFromDB, minimalExamTaskTree } from '../../utils/exam-utils.js';
import { dropDistributedParentTotals } from '../../utils/points-model.js';
import { refreshSolutionSourceState } from '../../utils/solution-source.js';
import { persistTaskArtifact, type TaskArtifact } from '../agents/persist-task-writes.js';
import { TaskType } from '../task-types/task-main.js';

export interface StepResult {
  /** The step did its work without failing. A skipped step is `ok` — there was nothing to do. */
  ok: boolean;
  /** The step had no input (e.g. no master solution uploaded) and did nothing. */
  skipped: boolean;
}

export const SKIPPED: StepResult = { ok: true, skipped: true };

/**
 * Whether a top-level task carries at least one gradable node in its subtree — a root made purely of
 * task frames carries none. Used to know when a root's recursive enrichment (extraction finishes a root
 * exactly when this returns) should report its nodes done — see `collectSolutionUnitNodeIds`, which is
 * the actual SolutionProgress unit (see its doc comment).
 */
export function isSolutionUnitRoot(node: any): boolean {
  if (!node || typeof node !== 'object') return false;
  if (!isTaskFrameNode(node)) return true;
  const children = Array.isArray(node.subtasks) ? node.subtasks : [];
  return children.some(child => isSolutionUnitRoot(child));
}

/**
 * The actual unit `SolutionProgress.total`/`startedForTasks`/`createdForTasks` count (see its doc comment):
 * every gradable node, not just its root. Same "exclude pure task frames" rule as `isSolutionUnitRoot`,
 * applied per-node instead of only at the root — see `tree-generation-utils.ts`'s `solutionUnitNodeCount`,
 * the same concept against the post-structure-build tree shape.
 */
export function countSolutionUnitNodes(nodes: any[]): number {
  if (!Array.isArray(nodes)) return 0;
  let count = 0;
  const walk = (items: any[]) => {
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      if (!isTaskFrameNode(item)) count += 1;
      if (Array.isArray(item.subtasks) && item.subtasks.length > 0) walk(item.subtasks);
    }
  };
  walk(nodes);
  return count;
}

/**
 * Every gradable node's `_id` in a subtree rooted at `node` — what a solution-artifact extraction marks
 * done once the WHOLE root has been recursively enriched (see task-enrichment.ts), now that the unit is
 * the node rather than the root it lives under. Same "exclude pure task frames" rule as
 * `countSolutionUnitNodes`, collecting ids instead of counting. `node` here is the pre-enrichment task
 * record (already has `_id` — these steps enrich BUILT task records, which have one).
 */
export function collectSolutionUnitNodeIds(node: any): string[] {
  const ids: string[] = [];
  const walk = (item: any) => {
    if (!item || typeof item !== 'object') return;
    if (!isTaskFrameNode(item) && typeof item._id === 'string' && item._id) ids.push(item._id);
    if (Array.isArray(item.subtasks) && item.subtasks.length > 0) item.subtasks.forEach(walk);
  };
  walk(node);
  return ids;
}

/** Extraction-shape counterpart of `needsAnswer === false` (see build-exam-tasks). */
function isTaskFrameNode(node: any): boolean {
  return (
    String(node?.taskType ?? '')
      .trim()
      .toUpperCase() === TaskType.TASK_FRAME
  );
}

/**
 * Every node in the tree, at any depth — the unit count of the structure step, whose OCR pass marks one unit
 * per node. Walks `subtasks` (the raw extraction shape) rather than `subTasks`, because it counts the agent's
 * output before `buildExamTasksFromRecursiveStructure` has turned it into task records.
 */
export function countRecursiveTasks(nodes: any[]): number {
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let count = 0;
  const walk = (items: any[]) => {
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      count += 1;
      if (Array.isArray(item.subtasks) && item.subtasks.length > 0) walk(item.subtasks);
    }
  };
  walk(nodes);
  return count;
}

/**
 * How many tasks carry usable extracted content for a field. An image region counts even without
 * text: a master solution that is a drawing, or a criteria table captured as an image, is perfectly
 * usable — so this must not be text-only, or those extractions get rejected as empty.
 */
export function countExtractedFieldEntries(nodes: any[], field: 'masterSolutionContent' | 'gradingCriteria'): number {
  const bbxField = field === 'masterSolutionContent' ? 'masterSolutionBoundingBoxes' : 'gradingBoundingBoxes';
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;
  let count = 0;
  const walk = (items: any[]) => {
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const value = typeof item?.[field] === 'string' ? item[field].trim() : '';
      const hasImageBox =
        Array.isArray(item?.[bbxField]) && item[bbxField].some((b: any) => b?.contentType === 'image');
      if (value.length > 0 || hasImageBox) count += 1;
      if (Array.isArray(item?.subtasks) && item.subtasks.length > 0) walk(item.subtasks);
    }
  };
  walk(nodes);
  return count;
}

export function serializeTaskTree(tasksTree: any[]): any[] {
  return (Array.isArray(tasksTree) ? tasksTree : []).map(task =>
    typeof task?.toObject === 'function' ? task.toObject() : task,
  );
}

/** Accumulates LLM cost during a step, per kind of LLM call, and writes it to the exam once, at the end. */
export function createCostTracker(examId: string) {
  const buckets = new Map<string, { total: number; count: number }>();

  return {
    addCost(cost: unknown, source: string): void {
      const numericCost = Number(cost ?? 0);
      if (!Number.isFinite(numericCost) || numericCost <= 0) return;
      const bucket = buckets.get(source) ?? { total: 0, count: 0 };
      bucket.total += numericCost;
      bucket.count += 1;
      buckets.set(source, bucket);
    },
    async flush(): Promise<void> {
      await Promise.all(
        [...buckets.entries()].map(([source, { total, count }]) => applyCostDelta(examId, source, total, count)),
      );
    },
  };
}

export interface StepInput {
  exam: any;
  /** The confirmed task structure in the intermediate tree shape the extraction agents expect. */
  taskRecords: any[];
  /** `taskRecords` reduced to what agent prompts need. */
  minimalTasksJson: string;
}

/**
 * Loads the exam plus its current task structure. Every step that enriches tasks starts here, so the
 * teacher's latest edits are always the basis — there is no stale in-memory snapshot to go wrong.
 */
export async function loadStepInput(examId: string): Promise<StepInput | null> {
  const examCollection = await getCollection(Exam as any);
  const exam = await examCollection.findOne({ _id: examId } as any);
  if (!exam) {
    logger.error({ examId }, 'Pipeline step: exam not found');
    return null;
  }

  const taskRecords = examTaskTreeFromDB((exam.tasks || []) as any[]);
  return {
    exam,
    taskRecords,
    minimalTasksJson: JSON.stringify({ tasks: minimalExamTaskTree(taskRecords) }),
  };
}

/**
 * Writes back only the fields the extraction of ONE artifact owns, so master-solution and grading-scheme
 * extraction can run at the same time.
 *
 * Both steps rebuild the ENTIRE task tree in memory — they share `buildExamTasksFromRecursiveStructure` — and
 * that is fine, because the rebuild is derived from the same stored structure either way. What was not fine
 * was persisting all of it: each step wrote the whole `tasks` array, so whichever finished second replaced
 * the other's artifact with its own (stale) copy of it. They could only ever be serialised.
 *
 * Their field sets are genuinely disjoint, which is what makes the narrow write possible rather than merely
 * smaller: master-solution extraction has something to say about `masterSolution` and nothing else, and
 * grading-scheme extraction about `gradingScheme` plus the `declaredMaxPoints` that `dropDistributedParentTotals`
 * clears. Neither changes the SHAPE of the tree; only the structure step does that.
 *
 * DROPPING THE FIXED PARENT TOTALS BELONGS TO THE GRADING SCHEME, and only to it. This used to take a
 * `hasGradingScheme` flag — a question about the exam's STATE rather than about which step ran — and drop them for
 * either artifact, which was correct while the whole tree was written back. It cannot be correct here: the write is
 * narrowed to the fields of `artifact`, and `declaredMaxPoints` is not one of the master solution's, so the drop was
 * computed and then thrown away. The comment claimed both things at once.
 *
 * Nothing is lost by narrowing it, because both producers of a grading scheme already do this where they write:
 * an upload here (parent totals become derived the moment a scheme is in place) and a generation per root in
 * `gs-generation-agent.ts`. A master-solution re-upload on an exam that already has a scheme therefore has nothing
 * left to drop — and could not have, since fixed totals are written by the STRUCTURE step, which a confirmed exam
 * never re-runs (`structureConfirmationState` is never set back to false).
 */
export async function persistExtractedArtifact(
  examId: string,
  examTasks: any[],
  artifact: TaskArtifact,
): Promise<void> {
  const serialized = serializeTaskTree(examTasks);
  if (artifact === 'gradingScheme') dropDistributedParentTotals(serialized as any);

  // WHICH tasks the upload actually changed, recorded on the progress record for the run's closure to act
  // on. A re-upload rebuilds the whole tree; without this, "the master solution was re-extracted" would have
  // to be treated as "every task changed", and a one-task correction would regenerate every grading scheme
  // and regrade the whole exam.
  const { changedTaskIds } = await persistTaskArtifact(examId, serialized, artifact);
  if (changedTaskIds.length > 0) await addSolutionChangedTasks(examId, artifact, changedTaskIds);
  await refreshSolutionSourceState(examId);
}
